import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
// material
import {
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Fade,
} from "@mui/material";

// graphql
import { useMutation } from "@apollo/client";
import { AdminGQLFunction } from "../../graphql/ADMIN";
import { UserGQLFunction } from "../../graphql/USER";

// component
import Iconify from "../../components/Iconify";

// ----------------------------------------------------------------------

export default function ListMoreMenu({ userState, id, admin = false }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  
  const [updateAdminState, { data: adminData, loading: adminLoading }] = useMutation(
    AdminGQLFunction.UPDATE_ADMIN_STATE
  );
  
  const [updateUserState, { data: userData, loading: userLoading }] = useMutation(
    UserGQLFunction.UPDATE_USER_STATE
  );

  const navigate = useNavigate();
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleState = async () => {
    if (admin) {
      await updateAdminState({
        variables: { id },
      });
    } else {
      // For users: 0 is Normal, 1 is Blocked (matches USER.USER_STATE order)
      await updateUserState({
        variables: { 
          id, 
          userState: userState === 0 ? 1 : 0 
        },
      });
    }
  };

  useEffect(() => {
    const data = admin ? adminData?.updateAdminState : userData?.updateUserState;
    const loading = admin ? adminLoading : userLoading;

    if (!loading && data) {
      enqueueSnackbar(data.msg, {
        TransitionComponent: Fade,
        variant: data.err ? "warning" : "success",
        style: {
          textAlign: 'center',
        }
      });
    }
  }, [adminLoading, userLoading, adminData, userData, enqueueSnackbar, admin]);

  return (
    <>
      <IconButton
        ref={ref}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={ref.current}
        onClose={(event) => {
          event.stopPropagation();
          setIsOpen(false);
        }}
        PaperProps={{
          sx: { width: 200, maxWidth: "100%" },
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          sx={{ color: "text.secondary" }}
          onClick={(event) => {
            event.stopPropagation();
            handleToggleState();
            setIsOpen(false);
          }}
        >
          <ListItemIcon>
            <Iconify
              icon={userState === 0 ? "mdi:person-block" : "mdi:account"}
              width={24}
              height={24}
            />
          </ListItemIcon>
          <ListItemText
            primary={userState === 0 ? t('common.block') : t('common.unblock')}
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>

        <MenuItem
          sx={{ color: "text.secondary" }}
          onClick={(event) => {
            event.stopPropagation();
            navigate(
              admin ? `/admin/info/edit/${id}` : `/user/info/edit/${id}`
            );
            setIsOpen(false);
          }}
        >
          <ListItemIcon>
            <Iconify icon="eva:edit-fill" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('common.edit')}
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
